import { useTranslation } from "react-i18next";
import useSnackBar from "../../../../components/Commons/SnackBar/useSnackBar";
import { useGridPreferences } from "../../../../hooks/useGridPreferences";
import { usePreferenceTypesStore } from "../../../../store/Preferences/PreferencesStore";
import { useColumnsWSLItems } from "./columns";
import { useCompanyStore } from "../../../../store/AuthStore";
import { useEffect, useState } from "react";
import GenericPromises from "../../../../api/GenericPromises";
import { GridColDef, GridRowsProp } from "@mui/x-data-grid";
import { WSLItem } from "../../../../interfaces/Stocks/wslitems";
import { PreferenceType } from "../../../../interfaces/Commons/Preferences/preferences";
import { ErrorDisplay } from "../../../../components/Commons/Error/ErrorDisplay";
import { Spinner } from "../../../../components/Commons/Spinner/Spinner";
import { Header } from "../../../../components/Header";
import { Box } from "@mui/material";
import { FilterTable } from "../../../../components/Filter";
import { set } from "date-fns";
import DataTableSticky from "../../../../components/Tables/GridTableMaterialUI/DataTableSticky";
import { DialogEntity } from "../../../../components/Dialogs/DialogEntity";
import { ViewWSLItems } from "../view";


export const TableWSLItems = () => {
  const [t] = useTranslation("global");
  const { showSnackBar, SnackbarComponent } = useSnackBar();
  const {
    LoadColumnsOrder,
    LoadColumnsVisible,
    LoadColumnsSort,
    VerifyPreferenceOrderByResourceRoute,
    VerifyPreferenceVisualByResourceRoute,
    VerifyPreferenceSortByResourceRoute,
  } = useGridPreferences();
  const { preferenceTypes } = usePreferenceTypesStore.getState();
  const { columnsWSLItems } = useColumnsWSLItems();
  const { companyState } = useCompanyStore();
  const [dataLoaded, setDataLoaded] = useState(false);
  const [isLoadingDataTable, setIsLoadingDataTable] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [isLoadedMyLocalPreferences, setIsLoadedMyLocalPreferences] = useState(false);
  const { GenericGetResource } = GenericPromises();
  const [myPreferences, setMyPreferences] = useState({});
  const [mySortPreference, setMySortPreference] = useState([]);
  const [WSLItemsData, setWSLItemsData] = useState<GridRowsProp>([]);
  const [columns, setColumns] = useState<GridColDef<WSLItem>[]>([]);
  const [errorMessage, setErrorMessage] = useState<string>('');
  const initFilter = '/wslitems?filter=';
  const [isLoadingData, setIsLoadingData] = useState(true);
  const [openDialogView, setOpenDialogView] = useState(false);
  const [WSLItemPayload, setWSLItemPayload] = useState<any>({});

  const myCustomColumns: GridColDef<any>[] = [
    { field: 'item', headerName: t("wslitems.fields.item"), headerClassName: 'header-grid-table', flex: 1 },
    { field: 'warehouse', headerName: t("wslitems.fields.warehouse"), headerClassName: 'header-grid-table', flex: 1 },
    { field: 'section', headerName: t("wslitems.fields.section"), headerClassName: 'header-grid-table', flex: 1 },
  ];

  const loadTablePreferences = async () => {
    // get preferencetypes in Store
    let hasPreferenceOrder: boolean = false;
    // if exists preferencetypes
    if (preferenceTypes.length) {
      // if exists order preferences
      let myPreferencesColumnsOrder = preferenceTypes.find((item: PreferenceType) => item.preference_type_name === "GridColumnsOrder");
      if (myPreferencesColumnsOrder) {
        try {
          const preference = await VerifyPreferenceOrderByResourceRoute(
            "/wslitems",
            myPreferencesColumnsOrder.preference_type_id ?? 0
          );
          if (preference) {
            let myColumns = await LoadColumnsOrder("/wslitems", myCustomColumns, preference?.preference_value);
            setColumns(myColumns);
            hasPreferenceOrder = true;
          }
        } catch (error) {
          showSnackBar(`Error fetching column order preferences:${error}`, "error");
        }
      }

      let myPreferencesColumnsVisual = preferenceTypes.find((item: PreferenceType) => item.preference_type_name === "GridColumnsVisibility");
      // if exists visual preferences
      if (myPreferencesColumnsVisual) {
        try {
          const preference = await VerifyPreferenceVisualByResourceRoute(
            "/wslitems",
            myPreferencesColumnsVisual.preference_type_id ?? 0
          );
          if (preference) {
            let myLocalPreferences = await LoadColumnsVisible("/wslitems", preference);
            setMyPreferences(myLocalPreferences);
          }
        } catch (error) {
          console.error("Error fetching column visual preferences:", error);
        }
      }

      let myPreferencesColumnsSort = preferenceTypes.find((item: PreferenceType) => item.preference_type_name === "GridColumnsSorting");
      // if exists sort preferences
      if (myPreferencesColumnsSort) {
        try {
          const preference = await VerifyPreferenceSortByResourceRoute(
            "/wslitems",
            myPreferencesColumnsSort.preference_type_id ?? 0
          );
          if (preference) {
            let myLocalPreferences = await LoadColumnsSort("/wslitems", preference);
            setMySortPreference(myLocalPreferences);
          }
        } catch (error) {
          console.error("Error fetching column sort preferences:", error);
        }
      }

    }
    if (!hasPreferenceOrder) {
      setColumns(columnsWSLItems);
    }
    setIsLoadedMyLocalPreferences(true);
  }

  const GetData = () => {
    GenericGetResource(`/wslitems`)
      .then((response) => {
        setWSLItemsData(response.data.data);
        setIsLoadingData(false);
      });
  }

  useEffect(() => {
    if (isLoadedMyLocalPreferences && WSLItemsData) {
      setDataLoaded(true);
      setIsLoadingDataTable(true);
    }
  }, [WSLItemsData, isLoadedMyLocalPreferences]);

  useEffect(() => {
    document.title = `${companyState?.comercial_name} - ${t("movementshistory.title")}`;
    const fetchData = async () => {
      try {
        await loadTablePreferences();
        GetData();
      } catch (err: any) {
        setErrorMessage(err);
        setHasError(true);
        showSnackBar(err, 'error');
      }
    };

    fetchData();
  }, []);

  return (
    <>
      {!dataLoaded && !hasError && <Spinner />}
      {hasError &&
        <ErrorDisplay
          errorMessage={errorMessage}
        />
      }
      {dataLoaded && !hasError &&
        <>
          <Header
            size="sm"
            title={t("wslitems.title")}
            child={
              <Box display={"flex"} flexDirection={"row-reverse"}>
                <FilterTable
                  onFilterEmpty={GetData}
                  setIsLoadingDataTable={setIsLoadingData}
                  setDataTable={setWSLItemsData}
                  showSnackBar={showSnackBar}
                  columnsTable={columns}
                  initFilter={initFilter}
                  resourceRoute="/wslitems"
                />
              </Box>
            }
          />
          {isLoadingDataTable &&
            <Box sx={{ height: 'calc(100vh - 180px)', width: '100%' }}>
              <DataTableSticky
                columns={columns}
                setColumns={setColumns}
                data={WSLItemsData ?? []}
                entityId={"wsl_item_id"}
                entity={`WSLItems`}
                preferences={myPreferences}
                namePreferences={"/wslitems"}
                nameOrderColumns={"/wslitems"}
                sortPreference={mySortPreference}
                isLoading={isLoadingData}
                isChildren
                setOpenDialog={setOpenDialogView}
                setDataRow={setWSLItemPayload}
              />
            </Box>
          }
          <DialogEntity
            content={
              <ViewWSLItems
                WSLItemPayload={WSLItemPayload}
                setOpenDialog={setOpenDialogView}
              />
            }
            open={openDialogView}
            setOpen={setOpenDialogView}
            title={<Header title={t("wslitems.title-view")} size='sm' />}
          />
        </>
      }
      <SnackbarComponent />
    </>
  )
}
